import React, { useState } from 'react';
import EmptyState from '../../../components/ui/EmptyState';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { scheduleService } from '../../../services/scheduleService';

const TYPE_BG = {
  paper: 'bg-primary-subtle border-primary/30',
  keynote: 'bg-accent-subtle border-accent/30',
  invited: 'bg-violet-50 border-violet-200 dark:bg-violet-950/50 dark:border-violet-800',
  plenary: 'bg-primary-subtle border-primary/30',
  panel: 'bg-muted border-border',
  break: 'bg-warning-subtle border-warning-border',
  social: 'bg-pink-50 border-pink-200 dark:bg-pink-950/50 dark:border-pink-800',
  opening: 'bg-success-subtle border-success-border',
  closing: 'bg-success-subtle border-success-border',
  workshop: 'bg-secondary border-secondary',
  tutorial: 'bg-cyan-50 border-cyan-200 dark:bg-cyan-950/50 dark:border-cyan-800',
  poster: 'bg-lime-50 border-lime-200 dark:bg-lime-950/50 dark:border-lime-800',
  other: 'bg-muted border-border',
};

const ScheduleOverview = ({ conferenceId, sessions, rooms, conferenceDays, onRefresh }) => {
  const { t } = useTranslation('conferences');
  const k = (key, opts) => t(`managementDashboard.programTab.overview.${key}`, opts);

  const [selectedDay, setSelectedDay] = useState(conferenceDays?.[0]?.date || '');
  const [publishing, setPublishing] = useState(false);

  const daySessions = sessions?.filter(s => s?.sessionDate === selectedDay) || [];

  // Collect unique time slots for the day
  const timeSlots = [...new Set(daySessions?.map(s => s?.startTime?.substring(0, 5)))]?.sort();

  const findSession = (timeSlot, roomId) => {
    return daySessions?.find(s =>
      s?.startTime?.substring(0, 5) === timeSlot && s?.roomId === roomId
    );
  };

  // Sessions without a room (breaks, etc)
  const noRoomSessions = daySessions?.filter(s => !s?.roomId);

  const handlePublishAll = async () => {
    if (!window.confirm(k('confirmPublishAll'))) return;
    try {
      setPublishing(true);
      await scheduleService?.publishAllSessions(conferenceId);
      onRefresh?.();
    } catch (err) {
      console.error('Publish all error:', err);
    } finally {
      setPublishing(false);
    }
  };

  const formatTime = (t) => t?.substring(0, 5);

  const publishedCount = sessions?.filter(s => s?.isPublished)?.length || 0;
  const totalCount = sessions?.length || 0;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 overflow-x-auto">
          {conferenceDays?.map(day => (
            <button
              key={day.date}
              onClick={() => setSelectedDay(day.date)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-250 ${
                selectedDay === day.date
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-border text-foreground hover:bg-muted'
              }`}
            >
              {k('day', { number: day.dayNumber })}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {k('publishedCount', { published: publishedCount, total: totalCount })}
          </span>
          <Button
            size="sm"
            variant="outline"
            iconName="Globe"
            iconPosition="left"
            onClick={handlePublishAll}
            loading={publishing}
          >
            {k('publishAll')}
          </Button>
        </div>
      </div>

      {daySessions?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg">
          <EmptyState compact icon="CalendarX" title={k('noSessions')} body={k('noSessionsBody', { defaultValue: 'Nothing is scheduled for this day yet.' })} />
        </div>
      ) : rooms?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg">
          <EmptyState compact icon="DoorOpen" title={k('addRoomsFirst')} body={k('addRoomsFirstBody', { defaultValue: 'Sessions are laid out by room, so add at least one room before scheduling.' })} />
        </div>
      ) : (
        <>
          {/* No-room sessions (breaks, etc) */}
          {noRoomSessions?.length > 0 && (
            <div className="space-y-1.5">
              {noRoomSessions?.map(session => (
                <div key={session?.id} className={`rounded-lg border px-4 py-2 text-center ${TYPE_BG[session?.sessionType] || TYPE_BG.other}`}>
                  <div className="flex items-center justify-center gap-3 text-sm">
                    <span className="font-mono text-xs opacity-70">
                      {formatTime(session?.startTime)} – {formatTime(session?.endTime)}
                    </span>
                    <span className="font-medium">{session?.title}</span>
                    {!session?.isPublished && (
                      <Icon name="EyeOff" size={12} className="opacity-50" title={k('notPublished')} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Grid */}
          <div className="overflow-x-auto bg-card border border-border rounded-lg">
            <div
              className="grid min-w-[600px]"
              style={{
                gridTemplateColumns: `80px repeat(${rooms?.length}, minmax(180px, 1fr))`,
              }}
            >
              {/* Header row */}
              <div className="p-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b-2 border-border bg-muted/50">
                {k('time')}
              </div>
              {rooms?.map(room => (
                <div key={room?.id} className="p-2 text-xs font-semibold text-foreground text-center border-b-2 border-border border-l border-border bg-muted/50">
                  {room?.name}
                </div>
              ))}

              {/* Time rows */}
              {timeSlots?.map(slot => (
                <React.Fragment key={slot}>
                  <div className="p-2 text-xs font-mono text-muted-foreground border-b border-border flex items-start pt-3">
                    {slot}
                  </div>
                  {rooms?.map(room => {
                    const session = findSession(slot, room?.id);
                    return (
                      <div key={room?.id} className="p-1.5 border-b border-border border-l min-h-[60px]">
                        {session ? (
                          <div className={`rounded-md border p-2 h-full ${TYPE_BG[session?.sessionType] || TYPE_BG.other}`}>
                            <div className="flex items-start justify-between gap-1">
                              <p className="text-xs font-semibold text-foreground line-clamp-2">{session?.title}</p>
                              {!session?.isPublished && (
                                <Icon name="EyeOff" size={10} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                              )}
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-1">
                              {formatTime(session?.startTime)} – {formatTime(session?.endTime)}
                            </p>
                            {session?.items?.length > 0 && (
                              <div className="mt-1.5 space-y-0.5">
                                {session?.items?.slice(0, 4)?.map(item => (
                                  <p key={item?.id} className="text-[10px] text-foreground/80 truncate">
                                    {item?.itemType === 'paper' && item?.paper
                                      ? `#${item?.paper?.paperNumber} ${item?.paper?.title}`
                                      : item?.customTitle
                                    }
                                  </p>
                                ))}
                                {session?.items?.length > 4 && (
                                  <p className="text-[10px] text-muted-foreground">
                                    {k('more', { count: session?.items?.length - 4 })}
                                  </p>
                                )}
                              </div>
                            )}
                            {session?.track && (
                              <p className="text-[10px] text-muted-foreground mt-1">{session?.track?.name}</p>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ScheduleOverview;
